

import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import {
    CellContext,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';

import { useNavigate } from 'react-router-dom';
import ErrorState from '../../../components/states/ErrorState';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { OrganizationLocationDto } from '../../../models/location/dto/location.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import CreateLocationForm from '../components/CreateLocationForm';
import { useGetLocations } from '../hooks/useGetLocations';

const columnHelper = createColumnHelper<OrganizationLocationDto>();

export default function LocationTable() {
    const { selectedOrganization } = useSelectedOrganization();
    // `useGetLocations` is `enabled: Boolean(organizationId)`. Until run 107 it
    // also carried `placeholderData: []`, which made this page the sharpest
    // instance of the class in the tree: a disabled query reported
    // `status: 'success'`, `isLoading: false` and `data: []`, so with no
    // organization selected this page printed "this organization has no clubs"
    // — a definitive claim about a question nobody asked, with no spinner to
    // suggest anything was pending. The placeholder is gone; this classifies.
    const { data, isInitialLoading, isError } = useGetLocations(selectedOrganization?.id);
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const navigation = useNavigate();

    const columns = useMemo(() => [
        columnHelper.accessor('id', {
            id: 'id',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    ID
                </Text>
            ),
            cell: (info: CellContext<OrganizationLocationDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('locations.name')}
                </Text>
            ),
            cell: (info: CellContext<OrganizationLocationDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        })
    ], [textColor]);
    const table = useReactTable({
        data: data || [],
        columns,
        initialState: {
            columnVisibility: {
                id: false
            }
        },
        state: {
            sorting
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('tabs.locations')}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    return (
        <PageCard title={translate('tabs.locations')} rightHeader={<CreateLocationForm />}>
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                                <Th key={'actions'}
                                    width={10}
                                    {...tableThProps}
                                    borderColor={borderColor}
                                    cursor='pointer'>

                                </Th>
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}
                                    {...tableRowProps}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                onClick={() => {
                                                    navigation(`/admin/locationDetails?location=${row.getValue('id')}`);
                                                }}
                                                key={cell.id}
                                                {...tableCellProps}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                {/* `unreadable`, not the raw `isError`: a background refetch
                    that fails over rows already on screen keeps them rather
                    than putting an alert under a populated table (R96-3). */}
                {readState === 'unreadable' && (
                    <ErrorState message={translate('locations.error_loading')} />
                )}
                {readState === 'answered' && !data?.length && (
                    <Text textAlign="center" py={8} color="gray.500">
                        {translate('locations.no_locations')}
                    </Text>
                )}
                <Loader isLoading={readState === 'loading'} />
            </Box>
        </PageCard>
    );
}